import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Search, Star, Clock, MapPin, ChevronDown, X,
  Leaf, Tag, Heart, Filter, ChevronRight, Zap, BadgePercent,
  Home as HomeIcon, ListOrdered, User, Flame, Award, Bike,
  Plus, Minus, ShoppingCart, ArrowRight, Check, UtensilsCrossed,
  Info, Share2, ThumbsUp, SlidersHorizontal, Loader2, AlertCircle,
} from "lucide-react";

// ─── THEME ───────────────────────────────────────────────────────────────────
const T = {
  primary:      "#f97316",
  primaryLight: "#fff7ed",
  primaryMid:   "#fed7aa",
  primaryDark:  "#ea580c",
  bg:           "#f9f9f9",
  white:        "#ffffff",
  text:         "#1c1c1e",
  textSub:      "#6b7280",
  textMuted:    "#9ca3af",
  border:       "#e5e7eb",
  green:        "#16a34a",
  greenLight:   "#dcfce7",
  red:          "#ef4444",
  yellow:       "#fbbf24",
};

const API_BASE = typeof window !== "undefined" && window.location.hostname !== "localhost" ? "https://quickry-ivrv.onrender.com/api" : "http://localhost:5000/api";

const CUISINE_FILTERS = [
  { id: "all",     label: "All"          },
  { id: "north",   label: "North Indian" },
  { id: "south",   label: "South Indian" },
  { id: "chinese", label: "Chinese"      },
  { id: "pizza",   label: "Pizza"        },
  { id: "biryani", label: "Biryani"      },
  { id: "burger",  label: "Burger"       },
  { id: "snacks",  label: "Snacks"       },
  { id: "sweets",  label: "Sweets"       },
  { id: "juice",   label: "Juice"        },
];

const SORT_OPTIONS = [
  { id: "relevance", label: "Relevance"         },
  { id: "rating",    label: "Rating"            },
  { id: "delivery",  label: "Delivery Time"     },
  { id: "cost_low",  label: "Cost: Low to High" },
  { id: "cost_high", label: "Cost: High to Low" },
];

// ─── HOOKS ───────────────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 390);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// ─── BOTTOM NAV ──────────────────────────────────────────────────────────────
function BottomNav() {
  const navigate = useNavigate();
  const [active, setActive] = useState("home");
  const items = [
    { id: "home",    icon: HomeIcon,    label: "Home",    path: "/"        },
    { id: "search",  icon: Search,      label: "Search",  path: "/restaurants" },
    { id: "orders",  icon: ListOrdered, label: "Orders",  path: "/orders"  },
    { id: "profile", icon: User,        label: "Profile", path: "/profile" },
  ];
  return (
    <div style={{ position:"fixed", bottom:0, left:0, right:0, background:"rgba(255,255,255,0.97)", backdropFilter:"blur(20px)", borderTop:`1px solid ${T.border}`, display:"flex", zIndex:200, paddingBottom:"env(safe-area-inset-bottom,0px)", boxShadow:"0 -2px 16px rgba(0,0,0,0.06)" }}>
      {items.map(item => {
        const Icon = item.icon;
        const isActive = active === item.id;
        return (
          <button key={item.id} onClick={() => { setActive(item.id); navigate(item.path); }} style={{ flex:1, background:"none", border:"none", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:3, padding:"10px 0 8px", color:isActive ? T.primary : T.textMuted, transition:"color .2s", fontFamily:"inherit" }}>
            <Icon size={21} strokeWidth={isActive ? 2.2 : 1.8} />
            <span style={{ fontSize:"0.62rem", fontWeight:isActive ? 700 : 400 }}>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── VEG INDICATOR ───────────────────────────────────────────────────────────
function VegDot({ isVeg, size = 14 }) {
  return (
    <div style={{ width:size, height:size, border:`1.5px solid ${isVeg ? T.green : T.red}`, borderRadius:2, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
      <div style={{ width:size*0.45, height:size*0.45, borderRadius:"50%", background:isVeg ? T.green : T.red }} />
    </div>
  );
}

// ─── RESTAURANT CARD ─────────────────────────────────────────────────────────
function RestaurantCard({ restaurant, onClick, isMobile }) {
  const [liked, setLiked] = useState(false);

  // Support both MongoDB (_id) and legacy (id) formats
  const rId = restaurant._id || restaurant.id;

  // Generate gradient based on restaurant name if no image
  const gradients = [
    "linear-gradient(135deg,#f97316,#fb923c)",
    "linear-gradient(135deg,#16a34a,#22c55e)",
    "linear-gradient(135deg,#dc2626,#f97316)",
    "linear-gradient(135deg,#b45309,#f59e0b)",
    "linear-gradient(135deg,#92400e,#d97706)",
    "linear-gradient(135deg,#7c3aed,#a855f7)",
    "linear-gradient(135deg,#0369a1,#0ea5e9)",
    "linear-gradient(135deg,#059669,#34d399)",
  ];
  const gradientBg = restaurant.imageBg || gradients[restaurant.name?.charCodeAt(0) % gradients.length] || gradients[0];

  return (
    <div
      onClick={() => restaurant.isOpen !== false && onClick(rId)}
      style={{ background:T.white, borderRadius:isMobile ? 16 : 18, overflow:"hidden", border:`1px solid ${T.border}`, cursor:restaurant.isOpen !== false ? "pointer" : "default", transition:"transform .2s, box-shadow .2s", position:"relative", opacity:restaurant.isOpen !== false ? 1 : 0.65 }}
      onMouseEnter={e => { if(restaurant.isOpen !== false && !isMobile) { e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow="0 12px 40px rgba(0,0,0,0.1)"; }}}
      onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="none"; }}
    >
      {/* Image area */}
      <div style={{ position:"relative", height:isMobile ? 150 : 180, background:gradientBg, overflow:"hidden" }}>
        {restaurant.image ? (
          <img src={restaurant.image} alt={restaurant.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
        ) : (
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <UtensilsCrossed size={48} color="rgba(255,255,255,0.3)" strokeWidth={1.2} />
          </div>
        )}

        {/* Promo badge */}
        {restaurant.promoted && (
          <div style={{ position:"absolute", top:10, left:10, background:"rgba(0,0,0,0.55)", backdropFilter:"blur(6px)", color:"#fff", fontSize:"0.62rem", fontWeight:700, padding:"3px 8px", borderRadius:6, letterSpacing:"0.5px" }}>
            PROMOTED
          </div>
        )}

        {/* Like button */}
        <button
          onClick={e => { e.stopPropagation(); setLiked(l => !l); }}
          style={{ position:"absolute", top:10, right:10, background:"rgba(255,255,255,0.9)", border:"none", borderRadius:"50%", width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}
        >
          <Heart size={15} color={liked ? T.red : T.textSub} fill={liked ? T.red : "none"} strokeWidth={2} />
        </button>

        {/* Offer strip */}
        {restaurant.offer && (
          <div style={{ position:"absolute", bottom:0, left:0, right:0, background:"linear-gradient(to top, rgba(0,0,0,0.7), transparent)", padding:"20px 12px 10px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:5, color:"#fff", fontSize:"0.72rem", fontWeight:700 }}>
              <BadgePercent size={12} color={T.yellow} />
              {restaurant.offer}
            </div>
          </div>
        )}

        {/* Closed overlay */}
        {restaurant.isOpen === false && (
          <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ background:"rgba(0,0,0,0.8)", color:"#fff", padding:"6px 16px", borderRadius:100, fontSize:"0.8rem", fontWeight:700 }}>Currently Closed</div>
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding:isMobile ? "12px 14px" : "14px 16px" }}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8, marginBottom:4 }}>
          <div style={{ fontWeight:700, fontSize:isMobile ? "0.95rem" : "1rem", color:T.text, lineHeight:1.3 }}>{restaurant.name}</div>
          <div style={{ display:"flex", alignItems:"center", gap:3, background:restaurant.rating >= 4.3 ? T.green : "#f59e0b", color:"#fff", padding:"3px 8px", borderRadius:8, flexShrink:0 }}>
            <Star size={11} fill="#fff" color="#fff" />
            <span style={{ fontSize:"0.78rem", fontWeight:700 }}>{restaurant.rating || "4.0"}</span>
          </div>
        </div>

        <div style={{ color:T.textSub, fontSize:"0.78rem", marginBottom:8, lineHeight:1.4 }}>
          {restaurant.cuisine}
        </div>

        {/* Meta row */}
        <div style={{ display:"flex", alignItems:"center", gap:0, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:4, color:T.textSub, fontSize:"0.75rem" }}>
            <Clock size={12} strokeWidth={2} />
            <span style={{ fontWeight:600 }}>{restaurant.deliveryTime || 30} min</span>
          </div>
          <div style={{ width:3, height:3, borderRadius:"50%", background:T.textMuted, margin:"0 8px" }} />
          <div style={{ display:"flex", alignItems:"center", gap:4, color:T.textSub, fontSize:"0.75rem" }}>
            <MapPin size={12} strokeWidth={2} />
            <span style={{ fontWeight:600 }}>{restaurant.area || "Akola"}</span>
          </div>
          <div style={{ width:3, height:3, borderRadius:"50%", background:T.textMuted, margin:"0 8px" }} />
          <div style={{ color:T.textSub, fontSize:"0.75rem", fontWeight:600 }}>
            ₹{restaurant.avgCost || restaurant.deliveryFee || 0} {restaurant.avgCost ? "for two" : "delivery"}
          </div>

          {restaurant.badge && (
            <div style={{ marginLeft:"auto", background:T.primaryLight, color:T.primary, fontSize:"0.65rem", fontWeight:700, padding:"2px 8px", borderRadius:100, border:`1px solid ${T.primaryMid}` }}>
              {restaurant.badge}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN RESTAURANT PAGE ────────────────────────────────────────────────────
export default function RestaurantPage() {
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────
  const [restaurants,    setRestaurants]    = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [search,         setSearch]         = useState("");
  const [activeCuisine,  setActiveCuisine]  = useState("all");
  const [activeSort,     setActiveSort]     = useState("relevance");
  const [activeFilters,  setActiveFilters]  = useState([]);
  const [showSort,       setShowSort]       = useState(false);
  const [scrolled,       setScrolled]       = useState(false);

  const width    = useWindowWidth();
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  const px       = isMobile ? 16 : isTablet ? 28 : 48;

  // ── Scroll listener ────────────────────────────────────────
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // ── Fetch restaurants from MongoDB ─────────────────────────
  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      setError(null);

      const q = new URLSearchParams();

      // Cuisine filter — map local ids to search terms
      const cuisineMap = {
        north: "North Indian", south: "South Indian",
        chinese: "Chinese", pizza: "Pizza",
        biryani: "Biryani", burger: "Burger",
        snacks: "Snacks", sweets: "Sweets", juice: "Juice",
      };
      if (activeCuisine !== "all") q.append("cuisine", cuisineMap[activeCuisine] || activeCuisine);

      // Search
      if (search.trim()) q.append("search", search.trim());

      // Sort
      const sortMap = {
        rating: "rating", delivery: "deliveryTime",
        cost_low: "costLow", cost_high: "costHigh",
      };
      if (activeSort !== "relevance" && sortMap[activeSort]) {
        q.append("sort", sortMap[activeSort]);
      }

      const res  = await fetch(`${API_BASE}/restaurants?${q}`);
      const data = await res.json();

      if (!data.success) throw new Error(data.message || "Failed to fetch restaurants");

      setRestaurants(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRestaurants(); }, [activeCuisine, activeSort]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => fetchRestaurants(), 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── Client-side filter (veg, rating, time) ─────────────────
  const filtered = restaurants
    .filter(r => {
      const matchVeg    = activeFilters.includes("pure_veg") ? r.isVeg : true;
      const matchRating = activeFilters.includes("rating4")  ? (r.rating >= 4.0) : true;
      const matchTime   = activeFilters.includes("under30")  ? (r.deliveryTime <= 30) : true;
      return matchVeg && matchRating && matchTime;
    })
    .sort((a, b) => {
      if (activeSort === "rating")    return b.rating - a.rating;
      if (activeSort === "delivery")  return a.deliveryTime - b.deliveryTime;
      if (activeSort === "cost_low")  return (a.avgCost || 0) - (b.avgCost || 0);
      if (activeSort === "cost_high") return (b.avgCost || 0) - (a.avgCost || 0);
      return 0;
    });

  const toggleFilter = (id) =>
    setActiveFilters(f => f.includes(id) ? f.filter(x => x !== id) : [...f, id]);

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={{ background:T.bg, minHeight:"100vh", fontFamily:"'Plus Jakarta Sans','Outfit',sans-serif", color:T.text, overflowX:"hidden", paddingBottom:isMobile ? 80 : 40 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        body { background:${T.bg}; -webkit-tap-highlight-color:transparent; }
        input::placeholder { color:${T.textMuted}; }
        button, input { font-family:inherit; }
        ::-webkit-scrollbar { display:none; }
        .scroll-x { overflow-x:auto; scrollbar-width:none; }
        .scroll-x::-webkit-scrollbar { display:none; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        .fu1{animation:fadeUp .4s .00s ease both}
        .fu2{animation:fadeUp .4s .06s ease both}
        .fu3{animation:fadeUp .4s .12s ease both}
        .fu4{animation:fadeUp .4s .18s ease both}
        .sort-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.4); z-index:300; display:flex; align-items:flex-end; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>

      {/* ══ STICKY HEADER ══ */}
      <div style={{ position:"sticky", top:0, zIndex:100, background:scrolled ? "rgba(255,255,255,0.97)" : T.white, backdropFilter:"blur(20px)", boxShadow:scrolled ? "0 2px 16px rgba(0,0,0,0.07)" : "none", transition:"box-shadow .25s" }}>

        {/* Top row */}
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:`14px ${px}px 10px` }}>
          <button onClick={() => navigate(-1)} style={{ background:"none", border:"none", cursor:"pointer", color:T.text, display:"flex", alignItems:"center", padding:4, borderRadius:8 }}>
            <ArrowLeft size={22} />
          </button>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.35rem", letterSpacing:"-1px", color:T.text }}>
            Quick<span style={{ color:T.primary }}>ry</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:4, background:T.primaryLight, border:`1px solid ${T.primaryMid}`, borderRadius:100, padding:"5px 10px", cursor:"pointer", marginLeft:4 }}>
            <MapPin size={12} color={T.primary} strokeWidth={2.5} />
            <span style={{ fontSize:"0.72rem", fontWeight:700, color:T.primary }}>Akola</span>
            <ChevronDown size={11} color={T.primary} strokeWidth={2.5} />
          </div>
        </div>

        {/* Search */}
        <div style={{ padding:`0 ${px}px 10px` }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, background:T.bg, border:`1.5px solid ${T.border}`, borderRadius:12, padding:"11px 16px" }}>
            <Search size={16} color={T.textMuted} strokeWidth={2} />
            <input
              style={{ background:"none", border:"none", outline:"none", color:T.text, fontSize:"0.88rem", flex:1 }}
              placeholder="Search restaurants or cuisines..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button onClick={() => setSearch("")} style={{ background:"none", border:"none", cursor:"pointer", color:T.textMuted, display:"flex", padding:0 }}>
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        {/* Cuisine filter tabs */}
        <div className="scroll-x" style={{ display:"flex", gap:8, padding:`0 ${px}px 10px` }}>
          {CUISINE_FILTERS.map(c => (
            <button key={c.id} onClick={() => setActiveCuisine(c.id)} style={{ flexShrink:0, background:activeCuisine === c.id ? T.primary : T.white, color:activeCuisine === c.id ? "#fff" : T.textSub, border:`1.5px solid ${activeCuisine === c.id ? T.primary : T.border}`, borderRadius:100, padding:"7px 16px", fontSize:"0.78rem", fontWeight:700, cursor:"pointer", transition:"all .2s", whiteSpace:"nowrap" }}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Filter + Sort bar */}
        <div className="scroll-x" style={{ display:"flex", alignItems:"center", gap:8, padding:`0 ${px}px 12px`, borderBottom:`1px solid ${T.border}` }}>
          <button onClick={() => setShowSort(true)} style={{ flexShrink:0, display:"flex", alignItems:"center", gap:5, background:T.white, border:`1.5px solid ${T.border}`, borderRadius:100, padding:"7px 14px", fontSize:"0.78rem", fontWeight:600, color:T.text, cursor:"pointer" }}>
            <SlidersHorizontal size={13} strokeWidth={2} />
            Sort
            <ChevronDown size={12} />
          </button>

          {[
            { id:"pure_veg", label:"Pure Veg",     icon:Leaf         },
            { id:"rating4",  label:"Rated 4+",     icon:Star         },
            { id:"under30",  label:"Under 30 min", icon:Clock        },
          ].map(f => {
            const Icon = f.icon;
            const isActive = activeFilters.includes(f.id);
            return (
              <button key={f.id} onClick={() => toggleFilter(f.id)} style={{ flexShrink:0, display:"flex", alignItems:"center", gap:5, background:isActive ? T.primaryLight : T.white, border:`1.5px solid ${isActive ? T.primary : T.border}`, borderRadius:100, padding:"7px 14px", fontSize:"0.78rem", fontWeight:isActive ? 700 : 500, color:isActive ? T.primary : T.textSub, cursor:"pointer", transition:"all .2s" }}>
                <Icon size={12} strokeWidth={2} />
                {f.label}
                {isActive && <X size={11} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* ══ CONTENT ══ */}
      <div style={{ maxWidth:1200, margin:"0 auto", padding:`20px ${px}px` }}>

        {/* Header */}
        <div className="fu1" style={{ marginBottom:16, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:isMobile ? "1.1rem" : "1.3rem", letterSpacing:"-0.5px" }}>
            Restaurants near you
          </div>
          <div style={{ fontSize:"0.78rem", color:T.textSub, fontWeight:500 }}>
            {loading ? "Loading..." : `${filtered.length} places`}
          </div>
        </div>

        {/* ── Loading State ── */}
        {loading && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"60px 20px", gap:16 }}>
            <Loader2 size={36} color={T.primary} className="spin" />
            <div style={{ color:T.textSub, fontSize:"0.88rem", fontWeight:500 }}>Finding restaurants in Akola...</div>
          </div>
        )}

        {/* ── Error State ── */}
        {!loading && error && (
          <div style={{ textAlign:"center", padding:"60px 20px" }}>
            <AlertCircle size={48} color="#ef4444" style={{ marginBottom:12 }} />
            <div style={{ fontWeight:700, fontSize:"1rem", marginBottom:6, color:T.text }}>Could not load restaurants</div>
            <div style={{ fontSize:"0.82rem", color:T.textSub, marginBottom:20 }}>{error}</div>
            <button
              onClick={fetchRestaurants}
              style={{ background:T.primary, color:"#fff", border:"none", borderRadius:12, padding:"10px 24px", fontWeight:700, fontSize:"0.88rem", cursor:"pointer", fontFamily:"inherit" }}
            >
              Try Again
            </button>
          </div>
        )}

        {/* ── Empty State ── */}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ textAlign:"center", padding:"60px 20px", color:T.textMuted }}>
            <UtensilsCrossed size={48} color={T.border} strokeWidth={1.2} style={{ marginBottom:16 }} />
            <div style={{ fontWeight:700, fontSize:"1rem", marginBottom:6, color:T.textSub }}>No restaurants found</div>
            <div style={{ fontSize:"0.82rem" }}>
              {search ? `No results for "${search}"` : "Try adjusting your filters"}
            </div>
          </div>
        )}

        {/* ── Restaurant Grid ── */}
        {!loading && !error && filtered.length > 0 && (
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)", gap:isMobile ? 14 : 18 }}>
            {filtered.map((r, i) => (
              <div key={r._id || r.id} className={`fu${Math.min(i+1,4)}`}>
                <RestaurantCard
                  restaurant={r}
                  onClick={(id) => navigate(`/restaurant/${id}`)}
                  isMobile={isMobile}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ══ SORT BOTTOM SHEET ══ */}
      {showSort && (
        <div className="sort-overlay" onClick={() => setShowSort(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:T.white, borderRadius:"20px 20px 0 0", width:"100%", maxWidth:520, margin:"0 auto", padding:"20px 0 32px" }}>
            <div style={{ width:36, height:4, borderRadius:100, background:T.border, margin:"0 auto 20px" }} />
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:"1.1rem", paddingLeft:20, marginBottom:16, letterSpacing:"-0.5px" }}>Sort by</div>
            {SORT_OPTIONS.map(opt => (
              <button key={opt.id} onClick={() => { setActiveSort(opt.id); setShowSort(false); }} style={{ width:"100%", background:"none", border:"none", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", cursor:"pointer", fontFamily:"inherit" }}>
                <span style={{ fontSize:"0.92rem", fontWeight:activeSort === opt.id ? 700 : 500, color:activeSort === opt.id ? T.primary : T.text }}>{opt.label}</span>
                {activeSort === opt.id && <Check size={18} color={T.primary} strokeWidth={2.5} />}
              </button>
            ))}
          </div>
        </div>
      )}

      {isMobile && <BottomNav />}
    </div>
  );
}
